import React, { FC, ReactNode } from 'react';
import { TableCell, TableCellProps as MuiTableCellProps } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles, WithTheme } from '@material-ui/core/styles';
import classNames from 'classnames';
import {
  AutoSizer,
  Column,
  InfiniteLoader,
  Table,
  TableCellProps,
  TableHeaderProps,
  TableProps,
  WindowScroller,
  ColumnProps,
} from 'react-virtualized';
import { isEven } from '../../utilities';

type VirtualizedTableProps = Extract<TableProps, { height: number; width: number }>;

interface IColumn extends ColumnProps {
  headerContentRenderer?: (tableHeaderProps?: TableHeaderProps) => ReactNode;
  cellContentRenderer?: (tableCellProps?: TableCellProps) => ReactNode;
  muiHeaderCellProps?: MuiTableCellProps;
  muiCellProps?: MuiTableCellProps;
}

interface IHeaderRenderer {
  headerRendererProps: TableHeaderProps;
  headerContentRenderer: IColumn['headerContentRenderer'];
  muiHeaderCellProps: IColumn['muiHeaderCellProps'];
}

interface ICellRenderer {
  cellRendererProps: TableCellProps;
  cellContentRenderer: IColumn['cellContentRenderer'];
  muiCellProps: IColumn['muiCellProps'];
}

interface IPropsEnhanced extends VirtualizedTableProps, WithTheme, WithStyles<typeof styles> {
  columns: IColumn[];
  total?: number;
  isRowLoaded?: () => boolean;
  onLoadMoreRows?: () => Promise<void>;
  spacing?: number;
  // AutoSizer doesn't calculate parent border widths
  outerBorderWidth?: number;
}

const styles = (theme: Theme) =>
  createStyles({
    table: {
      fontFamily: theme.typography.fontFamily,
    },
    flexContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    tableCell: {
      flex: 1,
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
  });

const VirtualizedTable: FC<IPropsEnhanced> = props => {
  const {
    classes,
    theme,
    columns,
    spacing,
    onLoadMoreRows,
    outerBorderWidth,
    total,
    ...tableProps
  } = props;
  const cellClasses = classNames(classes.tableCell, classes.flexContainer);

  function getTableProps() {
    const { className, rowClassName, ...rest } = tableProps;

    return {
      className: classNames(classes.table, className),
      rowClassName: classNames(classes.flexContainer, rowClassName),
      ...rest,
    } as TableProps;
  }

  function headerRenderer({
    headerRendererProps,
    headerContentRenderer,
    muiHeaderCellProps,
  }: IHeaderRenderer) {
    const renderer = headerContentRenderer
      ? headerContentRenderer(headerRendererProps)
      : headerRendererProps.label;

    return (
      <TableCell
        component="div"
        className={cellClasses}
        variant="head"
        style={{ height: tableProps.headerHeight - spacing }}
        {...muiHeaderCellProps}>
        {renderer}
      </TableCell>
    );
  }

  function cellRenderer({ cellRendererProps, cellContentRenderer, muiCellProps }: ICellRenderer) {
    const renderer = cellContentRenderer
      ? cellContentRenderer(cellRendererProps)
      : cellRendererProps.cellData;

    const height = (tableProps.rowHeight as number) - spacing;
    const backgroundColor = isEven(cellRendererProps.rowIndex)
      ? theme.palette.common.white
      : theme.palette.grey[100];

    return (
      <TableCell
        component="div"
        className={cellClasses}
        variant="body"
        style={{ height, backgroundColor }}
        {...muiCellProps}>
        {renderer}
      </TableCell>
    );
  }

  function renderColumn(columnProps: IColumn) {
    const {
      className,
      dataKey,
      cellContentRenderer,
      headerContentRenderer,
      muiHeaderCellProps,
      muiCellProps,
      ...rest
    } = columnProps;

    return (
      <Column
        key={dataKey}
        dataKey={dataKey}
        className={classNames(classes.flexContainer, className)}
        headerRenderer={headerRendererProps =>
          headerRenderer({ headerRendererProps, headerContentRenderer, muiHeaderCellProps })
        }
        cellRenderer={cellRendererProps =>
          cellRenderer({ cellRendererProps, cellContentRenderer, muiCellProps })
        }
        {...rest}
      />
    );
  }

  return (
    <InfiniteLoader
      loadMoreRows={onLoadMoreRows}
      rowCount={total}
      isRowLoaded={tableProps.isRowLoaded}>
      {({ onRowsRendered, registerChild }) => (
        <WindowScroller>
          {({ height, isScrolling, scrollTop, onChildScroll }) => (
            <AutoSizer disableHeight>
              {({ width }) => (
                <Table
                  autoHeight
                  height={height}
                  width={width - outerBorderWidth}
                  isScrolling={isScrolling}
                  scrollTop={scrollTop}
                  onScroll={onChildScroll}
                  ref={registerChild}
                  onRowsRendered={onRowsRendered}
                  {...getTableProps()}>
                  {columns.map(renderColumn)}
                </Table>
              )}
            </AutoSizer>
          )}
        </WindowScroller>
      )}
    </InfiniteLoader>
  );
};

VirtualizedTable.defaultProps = {
  isRowLoaded: () => undefined,
  onLoadMoreRows: () => undefined,
  total: 5000,
  spacing: 1,
  outerBorderWidth: 0,
};

export default withStyles(styles, { withTheme: true })(VirtualizedTable);
